/**
 * Public Sandbox access control.
 *
 * Sandbox users are restricted to a strict route allowlist. Anything else is
 * redirected at the React Router level, the protected component never mounts
 * which means there is no DOM/CSS overlay that can be removed via DevTools.
 *
 * Mirror this list on the backend (sandbox_guard.py) for full data protection.
 */

// Routes a sandbox user is allowed to visit.
// Match modes:
//   exact       , must equal pathname
//   prefix      , pathname starts with this string (e.g. "/sales-order/")
const SANDBOX_ALLOWED = [
  { path: '/sales-order/new',        mode: 'exact'  },
  { path: '/sales-order/',           mode: 'prefix' }, // /sales-order/:id
  { path: '/planner',                mode: 'exact'  }, // Order Pipeline (week/day/month)
  { path: '/dispatches',             mode: 'exact'  },
  { path: '/dispatch-view',          mode: 'exact'  },
  { path: '/dispatch/',              mode: 'prefix' }, // /dispatch/:id viewer
  { path: '/products',               mode: 'exact'  },
  { path: '/customers',              mode: 'exact'  },
  { path: '/supplier-codes',         mode: 'exact'  },
  { path: '/supplier-trace',         mode: 'exact'  },
  { path: '/cobranding',             mode: 'exact'  },
  { path: '/batches',                mode: 'exact'  },
  // Phase 1, Inwards / Production / Traceability (demo screenshots)
  { path: '/inwards/',               mode: 'prefix' },
  { path: '/production/',            mode: 'prefix' },
  { path: '/traceability/',          mode: 'prefix' },
  { path: '/reports/',               mode: 'prefix' },
  { path: '/profile',                mode: 'exact'  },
  { path: '/account',                mode: 'exact'  },
  { path: '/login',                  mode: 'exact'  },
  // Public marketing routes, always accessible
  { path: '/',                       mode: 'exact'  },
  { path: '/site',                   mode: 'prefix' },
  { path: '/brochure',               mode: 'prefix' },
  { path: '/case-study',             mode: 'prefix' },
  { path: '/founding-customer',      mode: 'exact'  },
  { path: '/strategy',               mode: 'exact'  },
  { path: '/brand',                  mode: 'exact'  },
  { path: '/logo-render',            mode: 'exact'  },
];

export const SANDBOX_DEFAULT_PATH = '/sales-order/new';

export function isSandboxAllowedPath(pathname) {
  for (const r of SANDBOX_ALLOWED) {
    if (r.mode === 'exact' && pathname === r.path) return true;
    if (r.mode === 'prefix' && pathname.startsWith(r.path)) return true;
  }
  return false;
}

/** Quick check helper, call with the user object */
export function isSandboxUser(user) {
  return Boolean(user?.is_sandbox || user?.email === 'demo@keystone.cloudvoro.com');
}

/**
 * Sidebar nav items visible to sandbox users.
 * Anything not in this set is hidden from the menu (and blocked at routing too).
 */
export const SANDBOX_SIDEBAR_PATHS = new Set([
  '/sales-order/new',
  '/dispatches',
  '/dispatch-view',
  '/customers',
  '/products',
  '/supplier-codes',
  '/supplier-trace',
  '/cobranding',
  '/batches',
  '/inwards/suppliers',
  '/inwards/supplier-codes',
  '/inwards/deliveries',
  '/inwards/milk-intake',
  '/inwards/silos',
  '/production/make-days',
  '/production/make-days/new',
  '/traceability/backward',
  '/traceability/forward',
  '/traceability/recall',
  '/traceability/events',
  '/reports/make-day-summary',
  '/reports/milk-intake-usage',
  '/reports/supplier-lot-usage',
  '/reports/backward-trace',
  '/reports/forward-trace',
]);
